import { z } from "zod";
import { socialPlatformSchema, withoutLeadSocialFields } from "./social";

// T-PL-20 (ADR 0064): web searches the AI asks for while preparing a proposal.
// The worker runs them with OpenAI's `web_search` tool and keeps the results in
// the lead's custom fields, like the social searches of ADR 0034.

/** Custom-field key written by the web searches. */
export const LEAD_WEB_FIELDS = ["webSearches"] as const;

/** Searches per proposal and per lead: each one costs about 3 cents. */
export const WEB_SEARCHES_PER_PROPOSAL = 2;
export const WEB_SEARCHES_PER_LEAD = 4;
/** Sources kept per search and shown on the proposal card. */
export const WEB_SOURCES_PER_PROPOSAL = 5;

/** A page cited by the search, without OpenAI's tracking. */
export const webSourceSchema = z
  .object({
    url: z.string().url().max(500),
    title: z.string().max(200).nullable(),
  })
  .strict();
export type WebSource = z.infer<typeof webSourceSchema>;

/** A web search as the lead keeps it and as the model reads it. */
export const leadWebSearchSchema = z
  .object({
    query: z.string().min(1).max(200),
    status: z.enum(["found", "not_found", "failed"]),
    /** What the pages say about the business, or about each namesake found. */
    summary: z.string().max(800).nullable(),
    website: z.string().url().max(300).nullable(),
    location: z.string().max(200).nullable(),
    /** Instagram and Facebook pages of the business found on the web. */
    socialProfiles: z
      .array(z.object({ platform: socialPlatformSchema, url: z.string().url().max(300) }).strict())
      .max(4),
    sources: z.array(webSourceSchema).max(WEB_SOURCES_PER_PROPOSAL),
    searchedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type LeadWebSearch = z.infer<typeof leadWebSearchSchema>;

/** Web searches saved on a lead; malformed entries are skipped. */
export function readLeadWeb(customFields: Record<string, unknown>): { searches: LeadWebSearch[] } {
  const raw = customFields.webSearches;
  return {
    searches: Array.isArray(raw)
      ? raw.flatMap((entry) => {
          const parsed = leadWebSearchSchema.safeParse(entry);
          return parsed.success ? [parsed.data] : [];
        })
      : [],
  };
}

/** Comparable form of a web search: "Che Stampa  Palermo" and "che stampa palermo" are one. */
export function normalizeWebQuery(query: string): string {
  return query.trim().replace(/\s+/gu, " ").toLowerCase();
}

/** The lead's custom fields without the keys the AI searches write (ADR 0034, 0064). */
export function withoutLeadAiFields(
  customFields: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(withoutLeadSocialFields(customFields)).filter(
      ([key]) => !(LEAD_WEB_FIELDS as readonly string[]).includes(key),
    ),
  );
}
